import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const verificationAttemptedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    console.log('EmailVerification useEffect running, token:', token, 'attempted:', verificationAttemptedRef.current);
    
    // Check if token is provided
    if (!token) {
      setVerificationStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    // Only attempt verification once using ref to persist across re-renders
    if (!verificationAttemptedRef.current) {
      console.log('First verification attempt, setting ref to true');
      verificationAttemptedRef.current = true;
      verifyEmail(token);
    } else {
      console.log('Verification already attempted, skipping');
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    console.log('Starting email verification with token:', token);
    try {
      const response = await fetch(`${getApiUrl('/profile/verify-email')}?token=${token}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      console.log('Verification response:', { status: response.status, data });

      if (response.ok && data.success) {
        console.log('Verification successful, setting success state');
        setVerificationStatus('success');
        setMessage(data.message);
        setUserData(data.data.user);
        
        // Update the auth context if user is logged in
        if (isAuthenticated && user) {
          // Update user data in auth context to reflect verification status
          // No need to reload the page - just update the context
          console.log('Email verification successful, user data updated');
        }
      } else {
        console.log('Verification failed, setting error state');
        setVerificationStatus('error');
        setMessage(data.message || 'Email verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      setMessage('An error occurred during verification. Please try again.');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleCompleteProfile = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/');
    }
  };

  const handleResendVerification = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="email-verification-page">
      <div className="verification-container">
        <div className="logo">
          <div className="logo-icon">
            <img src="/main-logo-svg.svg" alt="Remote Birdy Logo" />
          </div>
          <h1 className="brand-name">Remote Birdy</h1>
        </div>

        {verificationStatus === 'loading' && (
          <div className="verification-content">
            <div className="loading-icon">
              <div className="spinner"></div>
            </div>
            <h2 className="verification-title">Verifying Your Email...</h2>
            <p className="verification-message">
              Please wait while we verify your email address.
            </p>
          </div>
        )}

        {verificationStatus === 'success' && (
          <div className="verification-content">
            <div className="success-icon">
              <svg width="60" height="60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h2 className="verification-title">Email Verified!</h2>
            <p className="verification-message">
              Congratulations! Your email address has been successfully verified. 
              You now have full access to all Remote Birdy features.
            </p>

            {userData && (
              <div className="user-info">
                <h3>Account Information</h3>
                <div className="user-details">
                  <div className="user-detail">
                    <span className="user-detail-label">Name:</span>
                    <span className="user-detail-value">{userData.name}</span>
                  </div>
                  <div className="user-detail">
                    <span className="user-detail-label">Email:</span>
                    <span className="user-detail-value">{userData.email}</span>
                  </div>
                  <div className="user-detail">
                    <span className="user-detail-label">Status:</span>
                    <span className="verified-badge">
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Verified
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="next-steps">
              <h3>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                What's Next?
              </h3>
              <ul>
                <li>Complete your profile setup</li>
                <li>Upload a profile photo</li>
                <li>Start browsing and posting ads</li>
                <li>Connect with other users</li>
                <li>Explore all Remote Birdy features</li>
              </ul>
            </div>

            <div className="action-buttons">
              <button onClick={handleGoHome} className="btn btn-primary">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                Go to Homepage
              </button>
              <button onClick={handleCompleteProfile} className="btn btn-secondary">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Complete Profile
              </button>
            </div>
          </div>
        )}

        {verificationStatus === 'error' && (
          <div className="verification-content">
            <div className="error-icon">
              <svg width="60" height="60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            
            <h2 className="verification-title">Verification Failed</h2>
            <p className="verification-message">
              {message}
            </p>

            <div className="error-details">
              <h3>Possible reasons:</h3>
              <ul>
                <li>The verification link has expired (links expire after 24 hours)</li>
                <li>The link has already been used</li>
                <li>The link is invalid or corrupted</li>
              </ul>
            </div>

            <div className="action-buttons">
              <button onClick={handleResendVerification} className="btn btn-primary">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                Request New Verification
              </button>
              <button onClick={handleGoHome} className="btn btn-secondary">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                Go to Homepage
              </button>
            </div>
          </div>
        )}

        <div className="footer-note">
          <p>If you have any questions, please contact our support team.</p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
